import React from 'react';
import { Page, Text, View, Document, StyleSheet, PDFViewer, PDFDownloadLink, Font, Image } from '@react-pdf/renderer';
import { BrowserView, MobileView } from 'react-device-detect';
import PdfPreview from 'ui-component/PdfPreview';
import { Stack, Typography } from '@mui/material';
import logo from 'assets/images/Logo-Aone-Final-Full.png';
import moment from 'moment';
import { ToWords } from 'to-words';

Font.register({ family: 'Noto Sans Lao', src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@master/hinted/ttf/NotoSansLao/NotoSansLao-Regular.ttf' });

const toWords = new ToWords({
    localeCode: 'en-US',
    converterOptions: { currency: false, ignoreDecimal: false, ignoreZeroCurrency: false, doNotAddOnly: false }
});

const formatCurrency = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    return `₭${Number(num).toLocaleString('en-US')}`;
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
    body: { paddingTop: 30, paddingBottom: 60, paddingHorizontal: 35, fontFamily: 'Noto Sans Lao' },
    title: { padding: 5, fontSize: 20, textAlign: 'center', fontWeight: 'bold' },
    section: { flexDirection: 'row', alignItems: 'center', fontSize: 9, marginBottom: 2 },
    label: { width: 80, fontWeight: 'bold' },
    value: { flex: 1 },
    logo: { position: 'absolute', top: 20, left: 35, width: 80 },
    table: { width: '100%', marginTop: 10 },
    rowTable: { flexDirection: 'row', fontSize: 9 },
    headerTable: { backgroundColor: '#f8f9fa', fontWeight: 'bold', borderTopWidth: 1, borderTopColor: '#000', borderTopStyle: 'solid' },
    // ── Column widths (UNCHANGED) ─────────────────────────────────────────────
    row0: { width: '25%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row1: { width: '10%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row2: { width: '30%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row3: { width: '15%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row4: { width: '20%', border: '1px solid #000', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' }
});

// ─── Document ─────────────────────────────────────────────────────────────────
const MyDocument = ({ data }) => (
    <Document>
        <Page size="A4" style={styles.body} wrap>
            <Image style={styles.logo} src={logo} />
            <Text style={styles.title}>Wallet Withdrawal</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15, marginBottom: 5 }}>
                <View style={{ width: '55%' }}>
                    <View style={styles.section}><Text style={styles.label}>Customer no.</Text><Text style={styles.value}>: {data?.customerID}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Mobile</Text><Text style={styles.value}>: {data?.customers?.mobile}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Customer</Text><Text style={styles.value}>: {data?.customers?.customerName}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Address</Text><Text style={styles.value}>: {`${data?.customers?.villages?.vill_name || ''}, ${data?.customers?.districts?.dr_name || ''}, ${data?.customers?.provinces?.pr_name || ''}`}</Text></View>
                    {data?.reference && (
                        <View style={styles.section}><Text style={styles.label}>Reference</Text><Text style={styles.value}>: {data?.reference}</Text></View>
                    )}
                </View>
                <View style={{ width: '40%' }}>
                    <View style={styles.section}><Text style={styles.label}>Date</Text><Text style={styles.value}>: {moment(data?.createdAt).format('DD-MM-yyyy HH:mm')}</Text></View>
                    <View style={styles.section}><Text style={{ fontWeight: 'bold', fontSize: 9 }}>Credit No. {data?.withdrawalID}</Text></View>
                </View>
            </View>
            {data?.description && (
                <View style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 9, fontWeight: 'bold' }}>Description: <Text style={{ fontWeight: 'normal' }}>{data?.description}</Text></Text>
                </View>
            )}
            {/* ── Table (UNCHANGED columns) ── */}
            <View style={styles.table}>
                <View style={[styles.rowTable, styles.headerTable]} fixed>
                    <Text style={styles.row0}>WithdrawalID</Text>
                    <Text style={styles.row1}>CustomerID</Text>
                    <Text style={styles.row2}>CustomerName</Text>
                    <Text style={styles.row3}>Type</Text>
                    <Text style={styles.row4}>Total(₭)</Text>
                </View>
                <View style={styles.rowTable} wrap={false}>
                    <Text style={[styles.row0, { fontSize: 10 }]}>{data?.withdrawalID}</Text>
                    <Text style={[styles.row1, { fontSize: 8 }]}>{data?.customerID}</Text>
                    <Text style={[styles.row2, { fontSize: 8 }]}>{data?.customers?.customerName}</Text>
                    <Text style={[styles.row3, { fontSize: 8 }]}>{data?.type}</Text>
                    <Text style={[styles.row4, { fontSize: 8 }]}>{formatCurrency(data?.payment)}</Text>
                </View>
                {/* Words + Total row */}
                <View style={styles.rowTable} wrap={false}>
                    <View style={{ width: '65%', border: '1px solid #000', borderRight: 'none', padding: 6, justifyContent: 'center' }}>
                        <Text style={{ fontSize: 8, textAlign: 'center' }}>{toWords.convert(data?.payment || 0)}</Text>
                    </View>
                    <View style={{ width: '15%', border: '1px solid #000', borderRight: 'none', padding: 6, justifyContent: 'center' }}>
                        <Text style={{ fontSize: 9, fontWeight: 'bold' }}>Total ₭</Text>
                    </View>
                    <View style={{ width: '20%', border: '1px solid #000', padding: 6, justifyContent: 'center' }}>
                        <Text style={{ fontSize: 9, textAlign: 'right' }}>{formatCurrency(data?.payment)}</Text>
                    </View>
                </View>
                {/* Reason row */}
                <View style={styles.rowTable} wrap={false}>
                    <View style={{ width: '15%', border: '1px solid #000', borderRight: 'none', padding: 6, justifyContent: 'center' }}>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', textAlign: 'center' }}>Reason</Text>
                    </View>
                    <View style={{ width: '85%', border: '1px solid #000', padding: 6, justifyContent: 'center' }}>
                        <Text style={{ fontSize: 9 }}>{data?.reason}</Text>
                    </View>
                </View>
            </View>
            {/* ── Signature Section ── */}
            <View style={{ marginTop: 25, flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                <View style={{ width: '48%' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Request By: {data?.CreateBy || '....................'}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : ............................................................</Text>
                </View>
                <View style={{ width: '48%' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Approve By: {data?.ApproveBy || '....................'}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : ............................................................</Text>
                </View>
            </View>
            {/* ── Company Info ── */}
            <View style={{ marginTop: 15, borderWidth: 1, borderColor: 'blue', borderRadius: 10, padding: 10 }} wrap={false}>
                <Text style={{ color: 'blue', fontSize: 8, fontWeight: 'bold' }}>A One Distributors Co., Ltd Nongbone Road, Naxay Village, Xaysettha District, Vientiane Lao PDR Mobile: 020 88871111</Text>
            </View>
        </Page>
    </Document>
);

// ─── Component ────────────────────────────────────────────────────────────────
const WithdrawalInvoicePDF = ({ data }) => {
    if (!data) return null;
    return (
        <>
            <PdfPreview document={<MyDocument data={data} />} fileName={data?.withdrawalID} />
        </>
    );
};

export default WithdrawalInvoicePDF;
